import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { formatPinNumber } from 'utils/personal-identity-number';
import { Box, CircularProgress } from '@mui/material';
import { Order } from 'models/order';
import { orderForActionModifyTemplate } from 'apollo/templates/order/query.graphql';
import { OrderInputType } from 'models/order-input/shared';
import { ActionOrderInput } from 'models/order-input/action-order';
import { ActionOrderContext } from './action-order-context';
import ActionOrder from './action-order.component';
import { restorationsToActionConstructions } from 'components/order-flow/shared/restoration-input-helper';
import { ActionSet } from 'models/action-set';
import { getActionSetTemplate } from 'apollo/templates/action-sets';
import { ConstructionProvider } from 'context/construction-context';

const ModifyActionOrder: FC = () => {
    const [order, setOrder] = useState<ActionOrderInput>({} as ActionOrderInput);
    const [fetchedOrder, setFetchedOrder] = useState<Order>({} as Order);
    const [isActionSetLoaded, setIsActionSetLoaded] = useState(false);
    const [areConstructionsHydrated, setAreConstructionsHydrated] = useState(false);
    const isLoading = !isActionSetLoaded || !areConstructionsHydrated;

    const params = useParams<{ id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [fetchOrder] = useLazyQuery(orderForActionModifyTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }: { order: Order }) => {
            if (order) {
                if (order.type !== 'ACTION_BASED') {
                    throw new Error('Order is not a acation based order');
                }
                setFetchedOrder(order);
                setOrder({
                    inputType: OrderInputType.MODIFY,
                    _id: order._id,
                    supplier: order.supplier,
                    customer: order.customer,
                    dentist: order.dentist,
                    historicalComments: order.historicalComments || [],
                    pricedRestorations: order.restorations || [],
                    currency: order.currency,
                    totalPrice: 0,
                    totalVat: 0,
                    // DD-3494: a patient-less order prefills the same empty patient a fresh wizard starts with.
                    patient: {
                        personalIdentityNumber: order.patient?.personalIdentityNumber
                            ? formatPinNumber(order.patient.personalIdentityNumber)
                            : '',
                        firstName: order.patient?.firstName || '',
                        lastName: order.patient?.lastName || '',
                        noPersonalIdentityNumber: order.patient?.noPersonalIdentityNumber || false,
                        patientCategory: order.patient?.category
                            ? {
                                  name: order.patient?.category,
                              }
                            : undefined,
                    },
                    constructions: [],
                    comment: '',
                    participants: {
                        linkedDentists: order.linkedDentists || [],
                        linkedClinics: order.linkedClinics || [],
                        customParticipantEmails: order.customParticipantEmails || [],
                    },
                    files: order.filesUrl || [],
                    sample: order.deliveryDetails.sample || { type: '', content: [], otherContent: '' },
                    deliveryDetails: {
                        date: order.deliveryDetails.date ? new Date(order.deliveryDetails.date) : null,
                        time: {
                            hour: order.deliveryDetails.time?.hour,
                            minute: order.deliveryDetails.time?.minute,
                        },
                        isPickUpAtClinic: order.deliveryDetails.isPickUpAtClinic ?? undefined,
                        pickUpLocation: order.deliveryDetails.pickUpLocation,
                        destination: order.deliveryDetails.destination,
                    },
                    labExceptions: order?.deliveryDetails.reason || {},
                    currentActionSet: {} as ActionSet,
                    attachedScannerCaseFiles: order.attachedScannerCaseFiles || [],
                } as ActionOrderInput);

                // Lab-filtered set (with labId): only the actions this lab has enabled in its price
                // list. Drives the "add action" picker so users can't add actions the lab doesn't offer.
                getActionSet({
                    variables: {
                        actionSetId: order.actionSet,
                        orgId: order.customer._id,
                        labId: order.supplier._id,
                    },
                });
                // Full set (no labId): every action in the set, used ONLY to hydrate the order's
                // existing constructions. An order may reference an action the lab has since disabled
                // in its price list; hydrating from the lab-filtered set dropped it, blanking the
                // construction's actions and crashing the lab-delivery step. The full set preserves it.
                getFullActionSet({
                    variables: {
                        actionSetId: order.actionSet,
                        orgId: order.customer._id,
                    },
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getActionSet] = useLazyQuery(getActionSetTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ actionSet }) => {
            setOrder((prev) => ({
                ...prev,
                currentActionSet: actionSet,
            }));
            setIsActionSetLoaded(true);
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const [getFullActionSet] = useLazyQuery(getActionSetTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ actionSet }) => {
            setOrder((prev) => ({
                ...prev,
                constructions: restorationsToActionConstructions(fetchedOrder, actionSet),
            }));
            setAreConstructionsHydrated(true);
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    useEffect(() => {
        fetchOrder({ variables: { orderId: params.id } });
    }, [params.id]);

    return (
        <ActionOrderContext.Provider value={{ order, setOrder }}>
            <ConstructionProvider>
                {isLoading || !order._id ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                        <Box>
                            <CircularProgress size={80} />
                        </Box>
                    </Box>
                ) : (
                    <ActionOrder />
                )}
            </ConstructionProvider>
        </ActionOrderContext.Provider>
    );
};

export default ModifyActionOrder;
